import { useRef, useState } from 'react';
import { ModuleListView } from '../modules/ModuleListView';
import { useNotify } from '../../components/notifications';
import { api } from '../../api/client';
import { runLoading } from '../../components/loading';
import { useHotkeys, formatCombo } from '../../components/hotkeys';

// ─────────────────────────────────────────────────────────────────────────
// Settings → External Data Import → MCL & SCL Limits
// ─────────────────────────────────────────────────────────────────────────
//   Bulk-imports the lab's internal limits spreadsheet (Lab Method · Lab
//   Analyte · DWV Analyte · MCL/MCLG/SCL/Alert/MRL + units · AnalyteNotes) into
//   dbo.MCLAndSCLLimits, then shows the stored limits in the shared grid.  The
//   Phase-3 limit-check engine reads from the same table.  Reads .xls/.xlsx/.xlsm.
// ─────────────────────────────────────────────────────────────────────────

interface Props { onHome: () => void; }

export default function LimitsPage({ onHome }: Props) {
  const notify = useNotify();
  const [reloadKey, setReloadKey] = useState(0);
  const fileInput = useRef<HTMLInputElement>(null);

  const onPickFile = () => fileInput.current?.click();
  const onFileChosen = async (ev: React.ChangeEvent<HTMLInputElement>) => {
    const file = ev.target.files?.[0];
    ev.target.value = '';
    if (!file) return;
    const form = new FormData();
    form.append('file', file);
    try {
      const res = await runLoading(
        api.post<{ data?: { imported: number; skipped: number; messages: string[] } }>(
          '/api/v1/edi-limits/import', form),
        { blocking: true, label: `Importing ${file.name}…` });
      const d = res.data?.data;
      if (d) {
        notify.success(`Imported ${d.imported} limit(s)${d.skipped ? `, skipped ${d.skipped}` : ''}.`);
        if (d.skipped && d.messages?.[1]) notify.warning(d.messages[1]);
        setReloadKey(k => k + 1);
      }
    } catch (e: unknown) {
      notify.error(
        (e as { response?: { data?: { error?: { message?: string } } } })
          .response?.data?.error?.message
        ?? (e as { message?: string }).message ?? 'Import failed.');
    }
  };

  // Bespoke "Import Limits" action → keyboard shortcut via the ONE shared
  // hotkey layer (standing rule 14). Mirrors the toolbar button's onClick.
  useHotkeys({ combo: 'alt+i', label: 'Import Limits', group: 'Limits', run: () => onPickFile() });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: 'var(--ai-bg)' }}>
      {/* Top strip = breadcrumb only. The Import Limits action AND the load-time chip
          now live on the ModuleListView toolbar below (owner request 2026-10-14), so
          there is no second toolbar and no duplicate "Loaded" chip here, and the empty
          band is gone. */}
      <div style={{ padding: '14px 20px 0' }}>
        <div style={{ fontSize: 11, color: 'var(--ai-muted)' }}>
          Home &nbsp;›&nbsp; Settings &nbsp;›&nbsp; External Data Import &nbsp;›&nbsp;
          <span style={{ color: 'var(--ai-blue)', fontWeight: 600 }}>MCL &amp; SCL Limits</span>
        </div>
        <input ref={fileInput} type="file" accept=".xls,.xlsx,.xlsm" style={{ display: 'none' }} onChange={onFileChosen} />
      </div>

      <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
        <ModuleListView
          key={`edi-limits-${reloadKey}`}
          moduleKey="edi-limits"
          group="External Data Import"
          navTitle="MCL & SCL Limits"
          onHome={onHome}
          // Import Limits rides the ModuleListView toolbar (left slot → sits next to
          // Refresh), so there is ONE toolbar with ONE load-time chip.
          toolbarExtras={
            <button className="ca-btn" onClick={onPickFile} title={`Import Limits (${formatCombo('alt+i')})`}>
              <i className="ti ti-upload" /> Import Limits
            </button>
          }
        />
      </div>
    </div>
  );
}
